import React, { useState } from 'react';
import qs from 'qs';
import { useNavigate } from 'react-router-dom';
import { Container, Box } from '@mui/material';
import ListHeader, { AddButton } from '../components/ListHeader';
import FilterSidebar from '../components/common/filters/FilterSidebar';
import ITable from '../uikit/itable/ITable';
import TemplateModal from '../components/tasks/TemplateModal/TemplateModal';
import { useSearchTerm } from '../hooks/useSearchTerm';
import { useSyncPageParams } from '../hooks/useSyncPageParams';
import { useUserSorts } from '../hooks/useUserSorts';
import { useGetAllPreferences } from '../services/react-query/preferences';
import { useGetAllTaskTemplate } from '../services/react-query/task_templates';
import { TaskAvailableColumns, TaskSortParam, useGetAllTasks, UseGetAllTasksParams } from '../services/react-query/tasks';
import { PREFERENCES_VALID_KEYS } from '../types/Preference';
import { Task, TaskItemQueryParams } from '../types/Task';
import { UserConfigurableModule } from '../types/User';
import { Loading } from '../uikit';
import { TaskColumns, TaskFilters } from '../utility/task';
import { listPageLayoutStyles, listPageTableWrapperStyles } from '../styles/sxStyling';
import { useIsMobile } from '../hooks/useIsMobile';
import { FilterState } from '../components/common/filters/interfaces';
import { useDownloadCsv } from '../hooks/useDownloadCsv';

export default function TaskList() {
  const navigate = useNavigate();
  const { currentPage, setCurrentPage } = useSyncPageParams();
  const [showModal, setShowModal] = useState(false);

  const [filterState, setFilterState] = useState<FilterState>(undefined);
  const { isMobile } = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);

  const { userSorts } = useUserSorts<TaskSortParam>(UserConfigurableModule.TASKS);

  const { data: taskTemplateData } = useGetAllTaskTemplate({ params: { per_page: 0 } });
  const taskTemplates = taskTemplateData?.data || [];

  const { data: preferenceData, isLoading: prefLoading } = useGetAllPreferences({
    params: {
      filters: {
        keys: [PREFERENCES_VALID_KEYS.EVENT_TYPE, PREFERENCES_VALID_KEYS.TASK_TYPE, PREFERENCES_VALID_KEYS.EVENT_LOCATION],
      },
    },
  });

  const baseParams = {
    search: useSearchTerm(),
    filters: filterState,
    sort: userSorts,
  };

  const taskQueryParams: UseGetAllTasksParams = {
    ...baseParams,
    page: currentPage,
  };

  const { data, isLoading: tasksLoading } = useGetAllTasks({ params: taskQueryParams, enabled: true });

  const { isDownloadingCsv, handleDownloadCsv } = useDownloadCsv({
    params: baseParams,
    controller: 'tasks',
  });

  if (tasksLoading || prefLoading) return <Loading />;

  const tasks = data?.data || [];
  const meta = data?.meta;

  const newWithTemplate = (templateUuid: string | null) => {
    const params: TaskItemQueryParams = {};
    if (templateUuid) params.template = templateUuid;
    navigate(`./new?${qs.stringify(params)}`);
  };

  return (
    <Container maxWidth="xl">
      <ListHeader
        title="Tasks"
        filtersOpen={sidebarOpen}
        onToggleFilters={() => setSidebarOpen((o) => !o)}
        rightActions={<AddButton onAdd={() => setShowModal(true)} />}
      />

      <Box sx={listPageLayoutStyles}>
        {/* sidebar filters */}
        {sidebarOpen && (
          <FilterSidebar
            moduleFilters={TaskFilters(preferenceData, taskTemplates)}
            module={UserConfigurableModule.TASKS}
            open={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            activeFilter={filterState}
            setActiveFilter={setFilterState}
          />
        )}

        {/* main table area */}
        <Box sx={listPageTableWrapperStyles}>
          <ITable<Task, TaskAvailableColumns>
            availableColumns={TaskColumns}
            data={tasks}
            module={UserConfigurableModule.TASKS}
            page={currentPage}
            count={meta?.total_pages ?? 1}
            isDownloadingCsv={isDownloadingCsv}
            onDownloadCsv={handleDownloadCsv}
            onPageChange={(_e, v) => setCurrentPage(v)}
          />
        </Box>
      </Box>

      <TemplateModal isOpen={showModal} onCancel={() => setShowModal(false)} onCreate={newWithTemplate} />
    </Container>
  );
}
